import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ExternalLink, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { documentService, type LibraryDocument } from '@/services/api/documentService';
import { QUERY_KEYS } from '@/services/queryClient';
import { drivePreviewUrl, folderNameById, formatFileSize, kindLabel } from '../documentUi';
import type { DocumentCategory } from '@/services/api/documentService';

dayjs.extend(relativeTime);

interface DocumentsPreviewDrawerProps {
  doc: LibraryDocument | null;
  categories: DocumentCategory[];
  open: boolean;
  onClose: () => void;
  onOpenExternal: (doc: LibraryDocument) => void;
}

const DocumentsPreviewDrawer: React.FC<DocumentsPreviewDrawerProps> = ({
  doc,
  categories,
  open,
  onClose,
  onOpenExternal,
}) => {
  const navigate = useNavigate();
  const tasksQuery = useQuery({
    queryKey: QUERY_KEYS.documentTasks(doc?.id || ''),
    queryFn: () => documentService.listDocumentTasks(doc!.id),
    enabled: open && Boolean(doc?.id),
  });

  if (!doc) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 960 } } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap title={doc.name}>
              {doc.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {kindLabel(doc.kind, doc.mimeType)} · {formatFileSize(doc.size)} ·{' '}
              {folderNameById(categories, doc.categoryId)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={18} />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ExternalLink size={14} />}
            onClick={() => onOpenExternal(doc)}
          >
            Open in Drive
          </Button>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 280,
            bgcolor: 'grey.100',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <iframe
            title={`Preview ${doc.name}`}
            src={drivePreviewUrl(doc)}
            style={{ width: '100%', height: '100%', border: 0 }}
            allow="autoplay"
          />
        </Box>

        <Divider />
        <Typography variant="subtitle2" fontWeight={700}>
          Used in tasks
        </Typography>
        {tasksQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (tasksQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Not linked to any tasks yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {(tasksQuery.data || []).map((t) => (
              <ListItem
                key={t.linkedDocumentId || t.taskId}
                sx={{ px: 0 }}
                secondaryAction={
                  t.status ? <Chip size="small" label={t.status} /> : undefined
                }
              >
                <ListItemText
                  primary={
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        onClose();
                        navigate(`/tasks?taskId=${t.taskId}`);
                      }}
                      sx={{ textAlign: 'left' }}
                    >
                      {t.taskRef ? `${t.taskRef} · ` : ''}
                      {t.title}
                    </Link>
                  }
                  secondary={t.linkedAt ? `Linked ${dayjs(t.linkedAt).fromNow()}` : undefined}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default DocumentsPreviewDrawer;
